import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Initialize theme from localStorage or default to 'dark'
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
      return savedTheme;
    }
    return 'dark';
  });

  const [flutterApp, setFlutterApp] = useState(null);
  const [isFlutterReady, setIsFlutterReady] = useState(false);
  const [isSettingThemeFromFlutter, setIsSettingThemeFromFlutter] = useState(false);
  const hasInitializedRef = useRef(false);

  // Listen for Flutter initialization
  useEffect(() => {
    const handleFlutterInitialized = (event) => {
      // Prevent duplicate initialization
      if (hasInitializedRef.current) {
        return;
      }

      // Flutter initialized successfully
      setFlutterApp(event.detail);
      setIsFlutterReady(true);
      hasInitializedRef.current = true;

      // Set up theme change listener
      if (event.detail && typeof event.detail.onThemeChanged === 'function') {
        event.detail.onThemeChanged((newTheme) => {
          setIsSettingThemeFromFlutter(true);
          setTheme(newTheme);
          localStorage.setItem('theme', newTheme);
          // Reset flag after a short delay to allow the change to propagate
          setTimeout(() => setIsSettingThemeFromFlutter(false), 100);
        });
      }

      // Get initial theme from Flutter but only if React doesn't have a saved theme
      if (event.detail && typeof event.detail.getThemeMode === 'function') {
        const initialFlutterTheme = event.detail.getThemeMode();
        const savedReactTheme = localStorage.getItem('theme');

        if (!savedReactTheme) {
          setTheme(initialFlutterTheme);
          localStorage.setItem('theme', initialFlutterTheme);
        } else {
          // Sync React's saved theme to Flutter instead
          event.detail.setThemeMode(savedReactTheme);
        }
      }
    };

    // Listen only on document to avoid duplicate events
    document.addEventListener('flutter-initialized', handleFlutterInitialized);

    return () => {
      document.removeEventListener('flutter-initialized', handleFlutterInitialized);
    };
  }, []);

  // Update theme (this will also update Flutter's theme)
  const updateTheme = useCallback((newTheme) => {
    // Don't update if this change is coming from Flutter to prevent loop
    if (isSettingThemeFromFlutter) {
      return;
    }

    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);

    // Update Flutter theme if app is ready
    if (isFlutterReady && flutterApp && typeof flutterApp.setThemeMode === 'function') {
      flutterApp.setThemeMode(newTheme);
    }
  }, [isFlutterReady, flutterApp, isSettingThemeFromFlutter]);

  // Toggle theme (cycles through light -> dark -> system)
  const toggleTheme = useCallback(() => {
    let newTheme;
    switch (theme) {
      case 'light':
        newTheme = 'dark';
        break;
      case 'dark':
        newTheme = 'system';
        break;
      case 'system':
        newTheme = 'light';
        break;
      default:
        newTheme = 'dark';
    }
    updateTheme(newTheme);
  }, [theme, updateTheme]);

  // Get the resolved theme (handles system theme resolution)
  const getResolvedTheme = useCallback(() => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme;
  }, [theme]);

  // Listen for system theme changes when in system mode
  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        // Force re-render when system theme changes - this triggers a re-render
      };

      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [theme]);

  // Apply theme to document
  useEffect(() => {
    const resolvedTheme = getResolvedTheme();
    document.documentElement.classList.toggle('dark', resolvedTheme === 'dark');
  }, [theme, getResolvedTheme]);

  const value = {
    theme,
    resolvedTheme: getResolvedTheme(),
    updateTheme,
    toggleTheme,
    isFlutterReady,
    flutterApp,
    // Theme state checkers
    isLight: getResolvedTheme() === 'light',
    isDark: getResolvedTheme() === 'dark',
    isSystem: theme === 'system'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};